import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { shopApi } from '../utils/shopApi';
import { bookingApi } from '../utils/bookingApi';
import { clearDashboardCache } from '../utils/dashboardCache';
import { clearPhoneCache, pendingCount } from '../utils/offline';
import { ArrowLeftIcon, ArrowPathIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { tilesFor, staffDocumentsOn, orderSections } from '../data/staffTiles';
import { bgActionCount } from './booking/admin/AdminBgNumbers';
import axios from 'axios';
import { hasFeature } from '../hooks/useFeature';
import { NOTE_ISSUES } from '../components/payments/MembershipPaymentWarning';

// What each badge means, for the Needs action list
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const NEEDS_ACTION = {
  chatUnread: n => `${plural(n, 'unread message')}`,
  communityReports: n => `${plural(n, 'Community shop report')} to look at`,
  approvalsWaiting: n => `${plural(n, 'gymnast')} waiting for approval`,
  bgToCheck: n => `${plural(n, 'BG number')} to check`,
  paymentsBehind: n => `${plural(n, 'membership')} behind on payments`,
  pendingOrders: n => `${plural(n, 'shop order')} to deal with`,
  routinesToCheck: n => `${plural(n, 'routine')} to check`,
};

const AdminDashboard = () => {
  const { user, updateUser, can, isStaff, isClubAdmin } = useAuth();
  const navigate = useNavigate();
  const bgOn = hasFeature(user, 'bgNumbers');
  const [pendingOrderCount, setPendingOrderCount] = useState(0);
  const [routinesToCheck, setRoutinesToCheck] = useState(0);
  const [paymentsBehind, setPaymentsBehind] = useState(0);
  const [bgToCheck, setBgToCheck] = useState(0);
  const [approvalsWaiting, setApprovalsWaiting] = useState(0);
  const [chatUnread, setChatUnread] = useState(0);
  const [communityReports, setCommunityReports] = useState(0);

  // Each badge only asks for what this person's access lets them see
  const shopOn = can('shop');
  const paymentsOn = can('payments');
  const membersOn = can('members');
  const trackingOn = can('skillTracking');
  const chatOn = hasFeature(user, 'messaging') && (can('communications') || can('welfare', 'MANAGE'));
  const communityOn = hasFeature(user, 'messaging') && hasFeature(user, 'communityShop') && can('communications', 'MANAGE');
  useEffect(() => {
    if (!isStaff) return;
    if (shopOn) {
      shopApi.getPendingOrderCount()
        .then(res => setPendingOrderCount(res.data.count))
        .catch(() => {});
    }
    if (paymentsOn) {
      bookingApi.getDelinquentMemberships()
        .then(res => setPaymentsBehind((res.data.memberships || []).filter(m => !m.paymentIssueAckedAt && !NOTE_ISSUES.includes(m.paymentIssue)).length))
        .catch(() => {});
    }
    if (membersOn && bgOn) {
      bookingApi.getAdminBgNumbers()
        .then(res => setBgToCheck(bgActionCount(res.data.rows || [])))
        .catch(() => {});
    }
    if (membersOn) {
      bookingApi.getApprovalsWaiting()
        .then(res => setApprovalsWaiting((res.data.gymnasts || []).length))
        .catch(() => {});
    }
    if (chatOn) {
      bookingApi.chatUnreadCount()
        .then(res => setChatUnread(res.data.count || 0))
        .catch(() => {});
    }
    if (communityOn) {
      bookingApi.communityReportCount()
        .then(res => setCommunityReports(res.data.count || 0))
        .catch(() => {});
    }
    if (trackingOn) {
      axios.get('/api/gymnast-routines/to-check')
        .then(res => setRoutinesToCheck(res.data.length))
        .catch(() => {});
    }
  }, [isStaff, shopOn, paymentsOn, membersOn, trackingOn, bgOn, chatOn, communityOn]);

  // Arranging sections: a working copy of the order, saved to the person's account on Done
  const [arranging, setArranging] = useState(null);
  const [saving, setSaving] = useState(false);
  const [arrangeError, setArrangeError] = useState('');
  const move = (i, by) => setArranging(order => {
    const next = [...order];
    [next[i], next[i + by]] = [next[i + by], next[i]];
    return next;
  });
  const saveOrder = async (order) => {
    setSaving(true);
    setArrangeError('');
    try {
      const res = await axios.put('/api/users/profile', { adminSections: order });
      updateUser({ ...user, adminSections: res.data.user.adminSections });
      setArranging(null);
    } catch {
      setArrangeError("Couldn't save the order. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const [clearing, setClearing] = useState(false);
  const clearCache = async () => {
    const waiting = pendingCount();
    const kept = waiting ? ` ${waiting} change${waiting === 1 ? '' : 's'} still waiting to send will be kept.` : '';
    if (!window.confirm(`Clear the copy of the app and pages saved on this device, then reload with fresh copies? You'll stay logged in.${kept}`)) return;
    setClearing(true);
    clearDashboardCache();
    await clearPhoneCache();
    window.location.reload();
  };

  if (!isStaff) {
    navigate('/dashboard', { replace: true });
    return null;
  }

  const Section = ({ title, children }) => (
    <div className="admin-hub__section">
      <div className="admin-hub__section-title">{title}</div>
      <div className="admin-hub__tiles">{children}</div>
    </div>
  );

  const badges = { pendingOrders: pendingOrderCount, routinesToCheck, paymentsBehind, bgToCheck, approvalsWaiting, chatUnread, communityReports };
  const Tile = ({ tile: { to, icon: Icon, label, className, badge: badgeKey } }) => {
    const badge = badges[badgeKey];
    return (
      <Link
        to={to}
        className={`dashboard-tile ${className || ''}${badge > 0 ? ' dashboard-tile--action' : ''}`}
        aria-label={badge > 0 ? `${label}: ${badge} need${badge === 1 ? 's' : ''} action` : undefined}
      >
        <Icon className="dashboard-tile__icon" />
        <span className="dashboard-tile__label">{label}</span>
        {badge > 0 && <span className="dashboard-tile__badge" aria-hidden="true">{badge}</span>}
        {badge > 0 && <span className="dashboard-tile__action" aria-hidden="true">Needs action</span>}
      </Link>
    );
  };

  // Sections in the person's own order, or the default order
  const sections = new Map();
  tilesFor({ can, isClubAdmin, staffDocumentsRequired: staffDocumentsOn(user), features: user?.features }).filter(t => !t.homeOnly).forEach(t => {
    if (!sections.has(t.section)) sections.set(t.section, []);
    sections.get(t.section).push(t);
  });
  const order = orderSections([...sections.keys()], user?.adminSections);

  // Everything waiting, at the top, from the tiles this person can see
  const waiting = order.flatMap(title => sections.get(title))
    .filter(t => t.badge && badges[t.badge] > 0)
    .map(t => ({ tile: t, text: NEEDS_ACTION[t.badge](badges[t.badge]) }));

  return (
    <div className="admin-hub">
      <div className="admin-hub__header">
        <button className="admin-hub__back" onClick={() => navigate('/dashboard')}>
          <ArrowLeftIcon style={{ width: 16, height: 16 }} /> Home
        </button>
        <h1 className="admin-hub__title">Admin</h1>
        {!arranging && (
          <button type="button" className="admin-hub__back admin-hub__arrange" onClick={() => setArranging(order)}>
            Arrange sections
          </button>
        )}
      </div>

      {!arranging && waiting.length > 0 && (
        <div className="admin-hub__waiting" role="region" aria-label="Needs action">
          <div className="admin-hub__waiting-title">Needs action</div>
          <ul className="admin-hub__waiting-list">
            {waiting.map(({ tile, text }) => (
              <li key={tile.id}>
                <Link to={tile.to} className="admin-hub__waiting-item">
                  <tile.icon className="admin-hub__waiting-icon" aria-hidden="true" />
                  <span>{text}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {arranging ? (
        <div className="admin-hub__arranging">
          <p className="admin-hub__arrange-hint">Move sections up or down. Only you see this order.</p>
          <ol className="admin-hub__arrange-list">
            {arranging.map((name, i) => (
              <li key={name} className="admin-hub__arrange-row">
                <span>{name}</span>
                <span className="admin-hub__arrange-buttons">
                  <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label={`Move ${name} up`}>
                    <ChevronUpIcon />
                  </button>
                  <button type="button" onClick={() => move(i, 1)} disabled={i === arranging.length - 1} aria-label={`Move ${name} down`}>
                    <ChevronDownIcon />
                  </button>
                </span>
              </li>
            ))}
          </ol>
          {arrangeError && <p className="admin-hub__arrange-error" role="alert">{arrangeError}</p>}
          <div className="admin-hub__arrange-actions">
            <button type="button" className="btn btn-primary" onClick={() => saveOrder(arranging)} disabled={saving}>
              {saving ? 'Saving…' : 'Done'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setArranging(null)} disabled={saving}>Cancel</button>
            <button type="button" className="btn btn-link" onClick={() => saveOrder([])} disabled={saving}>Reset to default</button>
          </div>
        </div>
      ) : order.map(title => (
        <Section key={title} title={title}>
          {sections.get(title).map(t => <Tile key={t.id} tile={t} />)}
        </Section>
      ))}

      <Section title="This device">
        <button type="button" className="dashboard-tile" onClick={clearCache} disabled={clearing}>
          <ArrowPathIcon className="dashboard-tile__icon" />
          <span className="dashboard-tile__label">{clearing ? 'Clearing…' : 'Clear cache'}</span>
        </button>
      </Section>
    </div>
  );
};

export default AdminDashboard;
